import { useEffect, useRef, useCallback } from 'react'
import { EditorView, keymap, lineNumbers, highlightActiveLineGutter, highlightSpecialChars, drawSelection, highlightActiveLine } from '@codemirror/view'
import { EditorState, Compartment } from '@codemirror/state'
import { yaml } from '@codemirror/lang-yaml'
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands'
import { syntaxHighlighting, defaultHighlightStyle, bracketMatching } from '@codemirror/language'
import { oneDark } from '@codemirror/theme-one-dark'
import { StateEffect, StateField } from '@codemirror/state'
import { Decoration, DecorationSet } from '@codemirror/view'

// Effect to update error lines
const setErrorLines = StateEffect.define<number[]>()

// State field to track error line decorations
const errorLineField = StateField.define<DecorationSet>({
  create() {
    return Decoration.none
  },
  update(decorations, tr) {
    decorations = decorations.map(tr.changes)
    for (const effect of tr.effects) {
      if (effect.is(setErrorLines)) {
        const errorLines = effect.value
        const decorationList: any[] = []

        for (const lineNum of errorLines) {
          const line = tr.state.doc.line(lineNum)
          decorationList.push(
            Decoration.line({ class: 'cm-error-line' }).range(line.from)
          )
        }

        decorations = Decoration.set(decorationList, true)
      }
    }
    return decorations
  },
  provide: f => EditorView.decorations.from(f)
})

// Custom theme for error highlighting
const errorLineTheme = EditorView.baseTheme({
  '.cm-error-line': {
    backgroundColor: 'rgba(239, 68, 68, 0.2)',
    borderLeft: '3px solid #ef4444'
  }
})

// Light theme styles
const lightTheme = EditorView.theme({
  '&': {
    backgroundColor: '#ffffff',
    color: '#1f2937'
  },
  '.cm-content': {
    caretColor: '#1f2937'
  },
  '.cm-cursor': {
    borderLeftColor: '#1f2937'
  },
  '.cm-gutters': {
    backgroundColor: '#f9fafb',
    color: '#6b7280',
    border: 'none',
    borderRight: '1px solid #e5e7eb'
  },
  '.cm-activeLineGutter': {
    backgroundColor: '#f3f4f6'
  },
  '.cm-activeLine': {
    backgroundColor: '#f9fafb'
  },
  // Selection styles - need higher specificity to override active line
  '.cm-selectionBackground': {
    backgroundColor: '#bfdbfe !important'
  },
  '&.cm-focused .cm-selectionBackground': {
    backgroundColor: '#93c5fd !important'
  },
  // Ensure selection is visible on active line
  '.cm-activeLine .cm-selectionBackground': {
    backgroundColor: '#93c5fd !important'
  }
})

interface YamlEditorProps {
  value: string
  onChange: (value: string) => void
  errorLines?: number[]
  placeholder?: string
  className?: string
  minHeight?: string
  darkMode?: boolean
}

export function YamlEditor({
  value,
  onChange,
  errorLines = [],
  placeholder = 'Enter YAML configuration...',
  className = '',
  minHeight = '200px',
  darkMode = false
}: YamlEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null)
  const viewRef = useRef<EditorView | null>(null)
  const themeCompartment = useRef(new Compartment())

  // Create update listener
  const updateListener = useCallback(() => {
    return EditorView.updateListener.of((update) => {
      if (update.docChanged) {
        onChange(update.state.doc.toString())
      }
    })
  }, [onChange])

  // Initialize editor
  useEffect(() => {
    if (!editorRef.current) return

    const theme = darkMode ? oneDark : lightTheme

    const state = EditorState.create({
      doc: value,
      extensions: [
        lineNumbers(),
        highlightActiveLineGutter(),
        highlightSpecialChars(),
        history(),
        drawSelection(),
        EditorState.allowMultipleSelections.of(true),
        syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
        bracketMatching(),
        highlightActiveLine(),
        keymap.of([...defaultKeymap, ...historyKeymap]),
        yaml(),
        themeCompartment.current.of(theme),
        errorLineField,
        errorLineTheme,
        updateListener(),
        EditorView.lineWrapping,
        EditorView.theme({
          '&': { minHeight },
          '.cm-scroller': { overflow: 'auto' },
          '.cm-content': { minHeight }
        }),
        placeholder ? EditorView.contentAttributes.of({ 'aria-placeholder': placeholder }) : []
      ]
    })

    const view = new EditorView({
      state,
      parent: editorRef.current
    })

    viewRef.current = view

    return () => {
      view.destroy()
      viewRef.current = null
    }
  }, []) // Only run on mount

  // Update value when prop changes (external updates)
  useEffect(() => {
    const view = viewRef.current
    if (!view) return

    const currentValue = view.state.doc.toString()
    if (value !== currentValue) {
      view.dispatch({
        changes: {
          from: 0,
          to: currentValue.length,
          insert: value
        }
      })
    }
  }, [value])

  // Update error lines
  useEffect(() => {
    const view = viewRef.current
    if (!view) return

    // Filter out invalid line numbers
    const validLines = errorLines.filter(
      line => line >= 1 && line <= view.state.doc.lines
    )

    view.dispatch({
      effects: setErrorLines.of(validLines)
    })
  }, [errorLines])

  // Update theme when darkMode changes
  useEffect(() => {
    const view = viewRef.current
    if (!view) return

    const theme = darkMode ? oneDark : lightTheme
    view.dispatch({
      effects: themeCompartment.current.reconfigure(theme)
    })
  }, [darkMode])

  return (
    <div
      ref={editorRef}
      className={`border rounded-md overflow-hidden ${className}`}
      style={{ minHeight }}
    />
  )
}

// Helper function to find line number for a value in YAML text
export function findLineNumberForValue(yamlText: string, searchValue: string): number | null {
  const lines = yamlText.split('\n')
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].includes(searchValue)) {
      return i + 1 // 1-indexed
    }
  }
  return null
}

// Helper to find line numbers for multiple values
export function findLineNumbersForValues(yamlText: string, searchValues: string[]): Map<string, number> {
  const result = new Map<string, number>()
  const lines = yamlText.split('\n')

  for (const searchValue of searchValues) {
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].includes(searchValue)) {
        result.set(searchValue, i + 1) // 1-indexed
        break
      }
    }
  }

  return result
}
